import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, useHistory } from 'react-router-dom';
import { AppBar, SpaceSelector } from '@ublend-npm/aulaui-next';

import AssignmentPage from '$pages/AssignmentPage';
import AssignmentsPage from '$pages/AssignmentsPage';
import StudentsPage from '$pages/StudentsPage';
import StudentPage from '$pages/StudentPage';
import ThemeProvider from '$components/ThemeProvider';
import Banner from '$components/Banner';

import Spinner from '$components/Spinner';
import Error from '$components/Error';
import ErrorPage from '$components/ErrorPage';
import * as TYPES from '$variables/types';
import ErrorBoundary from '$components/ErrorBoundary';
import { STAFF, STUDENT } from '$variables/constants';

import { MainContainer, SpinnerContainer, AppContainer } from './App.styled';
import COLORS from '$variables/colors';

const handleAppIconClick = () => {
  window.location.href = '/';
};

const App = ({
  isLoading,
  hasError,
  initializeApp,
  activeSpaces,
  currentSpace,
  changeCurrentSpace,
  user,
  viewAs,
  handleLogout,
  socketConnect,
  socketSubscribe,
}) => {
  useEffect(() => {
    const init = async () => {
      // eslint-disable-next-line no-restricted-globals
      const spaceId = location.pathname.split('/')[1];

      await initializeApp(spaceId);
      await socketConnect();
      await socketSubscribe();
    };

    init();
  }, []);

  const history = useHistory();
  const handleChangeSpace = async ({ newSpace }) => {
    await changeCurrentSpace({ newSpace });
    await socketSubscribe();
    history.push(`/${newSpace.id}`);
  };

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );
  }

  if (hasError) {
    return <ErrorPage />;
  }

  const branchUrl =
    'https://ublend-parse-storage.s3.amazonaws.com/021e27fb27cebe6af91b15483b3c58b7student_success_logo_color.png';

  const banner = viewAs === STAFF && (
    <Banner>
      Some actions are not available because you are viewing this space via
      staff access.
    </Banner>
  );

  const background = `linear-gradient(-90deg, ${COLORS.lightBlue} 0%, ${COLORS.darkBlue}  100%)`;

  return (
    <ThemeProvider>
      {banner}
      <AppContainer>
        <AppBar
          appName="Student Success"
          onAppIconClick={handleAppIconClick}
          appIconUrl={branchUrl}
          user={user}
          onLogout={handleLogout}
          background={background}
        >
          <SpaceSelector
            onSpaceSelect={handleChangeSpace}
            activeSpaces={activeSpaces}
            currentSpace={currentSpace}
          />
        </AppBar>
      </AppContainer>
      <ErrorBoundary>
        <MainContainer>
          <Switch>
            {viewAs === STUDENT
              ? [
                  <Route
                    key={0}
                    exact
                    path="/:spaceId"
                    component={StudentPage}
                  />,
                ]
              : [
                  <Route
                    key={0}
                    exact
                    path="/:spaceId/assignment/:assignmentId"
                    component={AssignmentPage}
                  />,
                  <Route
                    key={1}
                    exact
                    path="/:spaceId/students"
                    component={StudentsPage}
                  />,
                  <Route
                    key={2}
                    exact
                    path="/:spaceId/students/:studentId"
                    component={StudentPage}
                  />,
                  <Route
                    key={3}
                    exact
                    path="/:spaceId"
                    component={AssignmentsPage}
                  />,
                ]}
            <Route path="/" component={() => <Error type="notFound" />} />
          </Switch>
        </MainContainer>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

App.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  hasError: PropTypes.bool,
  initializeApp: PropTypes.func.isRequired,
  changeCurrentSpace: PropTypes.func.isRequired,
  currentSpace: TYPES.Space,
  activeSpaces: PropTypes.arrayOf(TYPES.Space),
  user: TYPES.User,
  viewAs: TYPES.ViewAs,
  handleLogout: PropTypes.func.isRequired,
  socketConnect: PropTypes.func.isRequired,
  socketSubscribe: PropTypes.func.isRequired,
};

App.defaultProps = {
  hasError: false,
  currentSpace: null,
  user: null,
  viewAs: null,
  activeSpaces: [],
};

export default App;
